import { Flex } from "@chakra-ui/react";
import { Col, Empty, Row } from "antd";
import CardDefault from "components/Cards/CardDefault";
import Container from "components/Container";
import ExportMenu from "components/ExportMenu";
import { useLoading } from "components/GlobalLoading";
import ModalSelectFieldsRelatorio from "components/ModalSelectFieldsRelatorio";
import { useTenantInfo } from "core/features/relatorios/hooks/useTenantInfo";
import useAssembleia from "features/assembleia/hooks/useAssembleia";
import useVotosPorPergunta from "features/assembleia/hooks/useVotosPorPergunta";
import {
  VotacaoPerguntaDto,
  VotacaoVotosPorPerguntaItem,
} from "features/assembleia/typings";
import { useCurrentTenant } from "features/auth/tenant.store";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import { useCallback, useState } from "react";
import PDFRelatorioVotos from "../Relatorios/components/PDFRelatorioVotos";
import { exportVotosToXLSX } from "../Relatorios/util/helper";
import ChartResultadoPergunta from "./components/ChartResultadoPergunta";
import ListaPerguntas from "./components/ListaPerguntas";
import { PermissaoEnum } from "core/resources/enums/permissao";
import useHasPermission from "core/features/permissions/hooks/useHasPermission";
import {
  RelatorioVotosFields,
  fieldsRelatorioVotosColumns,
} from "core/features/relatorios/typings";

interface AssembleiaResultadoProps {
  idAssembleia: string;
}

function AssembleiaResultado({ idAssembleia }: AssembleiaResultadoProps) {
  const { tenant } = useCurrentTenant();
  const {
    data: detalheAssembleia,
    isLoading: isLoadingAssembleia,
    isFetching: isFetchingAssembleia,
  } = useAssembleia(idAssembleia);

  const {
    isLoading: isLoadingVotos,
    isFetching: isFetchingVotos,
    refetch: refetchVotos,
  } = useVotosPorPergunta(idAssembleia, false);

  const { isFetching: isFetchingInfo, refetch: refetchInfo } =
    useTenantInfo(false);

  const [selectedPergunta, setSelectedPergunta] = useState<
    VotacaoPerguntaDto | undefined
  >();
  const [votosModalOpen, setVotosModalOpen] = useState(false);
  const [exportType, setExportType] = useState<"pdf" | "excel" | undefined>(
    undefined
  );

  const hasPermissionExportarResultado = useHasPermission(
    PermissaoEnum.Backoffice.Administrativo.Votacao.Resultado
  );

  const overallLoading =
    isLoadingAssembleia ||
    isFetchingAssembleia ||
    isLoadingVotos ||
    isFetchingInfo ||
    isFetchingVotos;

  const { onDownload: onExportVotosPdfInternal } = useDownloadRenderedPdf<
    React.ComponentProps<typeof PDFRelatorioVotos>
  >({
    fileName: detalheAssembleia?.nome
      ? `Relatório de Votos - ${detalheAssembleia.nome}`
      : "Relatório de Votos",
    Component: PDFRelatorioVotos,
    loading: isFetchingVotos || isFetchingInfo,
  });

  const handleExportVotosPdf = useCallback(
    async (selectedFields?: RelatorioVotosFields[]) => {
      if (detalheAssembleia && tenant) {
        const { data: tenantInfo } = await refetchInfo();
        const { data: votosPorPergunta } = await refetchVotos();

        await onExportVotosPdfInternal({
          votosPorPergunta: votosPorPergunta as VotacaoVotosPorPerguntaItem[],
          votacaoNome: detalheAssembleia.nome,
          selectedFields,
          reportBaseData: tenantInfo!,
        });
      }
    },
    [
      detalheAssembleia,
      tenant,
      refetchInfo,
      refetchVotos,
      onExportVotosPdfInternal,
    ]
  );

  const handleExportVotosXlsx = useCallback(
    async (selectedFields?: RelatorioVotosFields[]) => {
      const { data: votosPorPergunta } = await refetchVotos();

      if (detalheAssembleia && votosPorPergunta) {
        exportVotosToXLSX({
          data: votosPorPergunta as VotacaoVotosPorPerguntaItem[],
          votacaoNome: detalheAssembleia.nome,
          selectedFields,
        });
      }
    },
    [detalheAssembleia, refetchVotos]
  );

  useLoading(overallLoading);

  return (
    <Container pl={0} pb={0}>
      <>
        {hasPermissionExportarResultado ? (
          <Flex
            mb={4}
            justifyContent="flex-end"
            gridGap={3}
            className="hidden-print"
          >
            <ExportMenu
              onExportPdf={() => {
                setExportType("pdf");
                setVotosModalOpen(true);
              }}
              onExportarXlsx={() => {
                setExportType("excel");
                setVotosModalOpen(true);
              }}
            />
          </Flex>
        ) : null}
        <Row gutter={16}>
          <Col sm={8} className="hidden-print">
            {detalheAssembleia?.perguntas && (
              <ListaPerguntas
                onSelect={(p) => setSelectedPergunta(p)}
                onUnselect={() => setSelectedPergunta(undefined)}
                perguntas={detalheAssembleia.perguntas}
                selectedPergunta={selectedPergunta}
                isLoading={overallLoading}
              />
            )}
            {!detalheAssembleia?.perguntas && !overallLoading && (
              <CardDefault>
                <Empty description="Nenhuma pergunta encontrada" />
              </CardDefault>
            )}
          </Col>
          <Col sm={16}>
            {selectedPergunta && detalheAssembleia ? (
              <ChartResultadoPergunta
                pergunta={selectedPergunta}
                votacaoId={detalheAssembleia.id}
              />
            ) : (
              <CardDefault h="400px">
                <Flex justify="center" align="center" h="100%">
                  <Empty description="Selecione uma pergunta para visualizar o resultado." />
                </Flex>
              </CardDefault>
            )}
          </Col>
        </Row>
      </>

      <ModalSelectFieldsRelatorio<RelatorioVotosFields>
        visible={votosModalOpen}
        onClose={() => {
          setVotosModalOpen(false);
          setExportType(undefined);
        }}
        onSubmit={({ fields }) => {
          if (exportType === "pdf") {
            handleExportVotosPdf(fields as RelatorioVotosFields[]);
          } else if (exportType === "excel") {
            handleExportVotosXlsx(fields as RelatorioVotosFields[]);
          }
          setVotosModalOpen(false);
          setExportType(undefined);
        }}
        fields={fieldsRelatorioVotosColumns}
        title="Selecionar Campos para Relatório de Votos"
        includeAssinaturas={false}
      />
    </Container>
  );
}

export default AssembleiaResultado;
